/**
 * Sidebar — fixed-width left rail with brand block, three primary
 * tabs (文献动态 / 学术周报 / 更多), a footer with the language toggle
 * and a build-meta line. Three tabs total, NOT four — the 4th disease
 * tab from convention #4 lives INSIDE weekly reports as a section label,
 * not as a sidebar entry.
 *
 * The brand image lives in `pages/public/logo.png`; missing-image
 * gracefully falls back to the alt text.
 */

import { el } from "../dom";
import { getLang, subscribeLang, toggleLang, type Lang } from "../i18n";
import { getStore, on as onStore } from "../store";
import { navigate } from "../router";

type TabName = "daily" | "weeks" | "more";

interface TabSpec {
  name: TabName;
  hash: string;
  label: { zh: string; en: string };
}

const TABS: readonly TabSpec[] = [
  { name: "daily", hash: "#/daily", label: { zh: "文献动态", en: "Literature" } },
  { name: "weeks", hash: "#/weeks", label: { zh: "学术周报", en: "Weekly" } },
  { name: "more", hash: "#/more", label: { zh: "更多", en: "More" } },
];

export function sidebar(): HTMLElement {
  const langButtons = renderLangToggle();
  const tabsList = el("nav", { class: "sidebar-tabs", "aria-label": "主导航" }, [
    ...TABS.map((t) => tabButton(t)),
  ]);

  const root = el("aside", { class: "app-sidebar" }, [
    el("div", { class: "sidebar-brand" }, [
      el("img", {
        src: "/logo.png",
        alt: "北京大学第三医院胸外科",
        width: 40,
        height: 40,
        onerror: (ev) => {
          const target = ev.currentTarget as HTMLImageElement;
          if (target.dataset["fallback"] === "1") return;
          target.dataset["fallback"] = "1";
          target.replaceWith(
            el("div", { class: "sidebar-brand-fallback" }, "北医三院胸外科"),
          );
        },
      }),
      el("div", { class: "sidebar-brand-text" }, [
        el("span", { class: "sidebar-brand-name" }, "胸外科文献中心"),
        el("span", { class: "sidebar-brand-tag" }, "Thoracic Literature Hub"),
      ]),
    ]),
    tabsList,
    el("footer", { class: "sidebar-footer" }, [
      langButtons,
      el("span", { class: "sidebar-meta" }, "v0.1 · Round A"),
    ]),
  ]);

  const refreshActive = (): void => {
    const activeName = activeTabName();
    for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>(".sidebar-tab"))) {
      const name = btn.dataset["tab"];
      const isActive = name === activeName;
      btn.classList.toggle("is-active", isActive);
      // Phase 10 a11y: announce the active tab to screen readers via
      // `aria-current="page"`. `aria-current` is the modern ARIA hook
      // for "this is the current item in the navigation set"; using
      // it (instead of a custom attribute) lets assistive tech apply
      // its standard "you are here" announcement.
      if (isActive) btn.setAttribute("aria-current", "page");
      else btn.removeAttribute("aria-current");
    }
  };

  refreshActive();
  onStore(refreshActive);
  subscribeLang(refreshActive);

  return root;
}

function tabButton(tab: TabSpec): HTMLElement {
  return el(
    "button",
    {
      type: "button",
      class: "sidebar-tab",
      "data-tab": tab.name,
      onclick: (ev) => {
        navigate(tab.hash);
      },
    },
    [
      el("span", { class: "sidebar-tab-dot" }),
      el("span", { class: "sidebar-tab-label" }, tab.label.zh),
    ],
  );
}

function renderLangToggle(): HTMLElement {
  const rebuild = (lang: Lang): HTMLElement => {
    const mkBtn = (val: Lang, label: string): HTMLElement =>
      el(
        "button",
        {
          type: "button",
          class: `lang-toggle-btn${val === lang ? " is-active" : ""}`,
          // Phase 10 a11y: `aria-pressed` is the ARIA hook for a toggle
          // button — screen readers announce "pressed" / "not pressed"
          // instead of just announcing the visible label. The current
          // language always reflects which side is the active one.
          "aria-pressed": val === lang ? "true" : "false",
          onclick: () => toggleLang(),
        },
        label,
      );
    return el(
      "div",
      { class: "lang-toggle", role: "group", "aria-label": "语言切换" },
      [mkBtn("zh", "中"), mkBtn("en", "EN")],
    );
  };

  const container = el("div", { class: "lang-toggle-slot" }, [rebuild(getLang())]);
  subscribeLang((lang) => {
    const next = rebuild(lang);
    container.replaceChildren(next);
  });
  return container;
}

function activeTabName(): TabName | null {
  const route = getStore().route.name;
  if (route === "daily" || route === "dailyByDate") return "daily";
  if (route === "week" || route === "weeks") return "weeks";
  if (route === "more" || route === "changelog" || route === "article") return "more";
  return null;
}
